<template>

  <main :class="['schedule', {'is-loading': loading, 'is-future-month': isFutureMonth }]" ref="main">

    <div class="schedule__wrapper">

      <div class="schedule__hours">
        <span v-for="hr in hours" :key="hr"><span>{{ hr }}</span></span>
      </div>

      <div class="schedule__lines">
        <span v-for="hr in hours" :key="hr"></span>
      </div>

      <div
        v-for="day in daysInMonth"
        :key="day"
        :class="['schedule__day', {'is-today': isDateToday(day, date.month)}]"
        :ref="isDateToday(day, date.month) ? 'todayElement' : undefined"
      >

        <div class="schedule__day-date">
          <span>{{ day }}.{{ date.month }}</span>
          <span class="schedule__day-date-dayname">{{ dayName(day, date.month, date.year) }}</span>
        </div>

        <div v-for="room in rooms" :key="room" class="schedule__day-room">
          <div class="schedule__day-room-name">{{ room }}</div>

          <div class="schedule__day-room-bookings">

            <article
                v-for="booking in getBookings(room, day)"
                :key="booking"
                :class="
                  [
                    `schedule__day-room-booking`,
                    `schedule__day-room-booking--color-${(Math.round(booking.title.length % 6))}`
                  ]
                "
                :style="
                  {
                    width: `${timeToWidthAndMargin(booking.time_from, booking.time_to).width}%`,
                    marginLeft: `${timeToWidthAndMargin(booking.time_from, booking.time_to).marginLeft}%`
                  }
                "
            >
              <div class="schedule__day-room-booking-inner" @click="toggleModal(booking.id)">
                <strong>{{ booking.title }}</strong>
                <small>{{ booking.contact }}<template v-if="booking.text && booking.text.trim() !== ''"> | {{ booking.text }}</template></small>

                <div class="schedule__day-room-booking-tags">
                  <div v-if="booking.noise" class="schedule__day-room-booking-tag schedule__day-room-booking-tag--noise">noise</div>
                  <div v-if="booking.silence" class="schedule__day-room-booking-tag schedule__day-room-booking-tag--silence">silence</div>
                </div>
              </div>

              <button
                class="schedule__day-room-booking-remove"
                title="Remove booking"
                @click="removeBooking(booking.id)"
              ></button>
            </article>

          </div>

          <div class="schedule__day-room-triggers">
            <div
              v-for="hr in hours"
              :key="hr"
              @click="toggleModal(0, room, `${date.year}-${date.month}-${day}`, `${hr}:00`)"
            ></div>
          </div>

        </div>

      </div>

    </div>

    <div class="schedule__loading">
      <Loading v-if="loading"/>
    </div>
  </main>

</template>

<script>
import moment from 'moment-timezone'
import Loading from './Loading.vue'

const MIN_HOUR = 8;
const MAX_HOUR = 23;

export default {
  name: 'Schedule',
  components: {
    Loading
  },
  props: {
    date: Object,
    rooms: Array,
    bookings: Array,
    removeBooking: Function,
    toggleModal: Function,
    loading: Boolean
  },
  data: () => {
    return {
      hours: [...Array((MAX_HOUR + 1) - MIN_HOUR).keys()]
          .map(hr => hr + MIN_HOUR)
          .map(hr => String(hr).padStart(2, '0'))
    }
  },
  computed: {
    daysInMonth() {
      return moment(`${this.date.year}-${this.date.month}-01`, `YYYY-MM-DD`).tz('Europe/Prague').daysInMonth()
    },
    isCurrentMonth() {
      return parseInt(moment.tz('Europe/Prague').format(`M`)) === this.date.month;
    },
    isFutureMonth() {
      return parseInt(moment.tz('Europe/Prague').format(`M`)) < this.date.month;
    }
  },
  methods: {
    isDateToday(day, month) {
      return (day === parseInt(moment.tz('Europe/Prague').format('DD')) && month === parseInt(moment.tz('Europe/Prague').format('MM')))
    },
    dayName(day, month, year) {
      return moment(`${day}-${month}-${year}`, `D-M-YYYY`).tz('Europe/Prague').format(`ddd`)
    },
    getBookings(room, day) {
      return this.bookings.filter(el => {
        return room === el.room && day === parseInt(moment.unix(el.time_from).tz('Europe/Prague').format(`D`))
      })
    },
    timeToWidthAndMargin(timeFrom, timeTo) {
      const onePercentOfDay = 1600 / 100

      let width = (moment.unix(timeTo).tz('Europe/Prague').format('HHmm') - moment.unix(timeFrom).tz('Europe/Prague').format('HHmm')) / onePercentOfDay

      let marginLeft = (moment.unix(timeFrom).tz('Europe/Prague').format('HHmm') - 800) / onePercentOfDay
      marginLeft = (marginLeft < 0) ? 0 : marginLeft

      return {
        width: width,
        marginLeft: marginLeft
      }
    },
    scrollToToday() {
      setTimeout(() => {
        this.$refs.todayElement[0]?.scrollIntoView()
      }, 100)
    },
    scrollToTop() {
      this.$refs.main.scrollTop = 0
    }
  },
  watch: {
    loading: {
      handler: function () {
        (this.isCurrentMonth) ? this.scrollToToday() : this.scrollToTop()
      },
      deep: true
    }
  }
}
</script>

<style>
.schedule {
  width: 100%;
  overflow: auto;
  position: relative;
  height: calc(100vh - 100px);
}

.schedule.is-loading {
  overflow: hidden;
}

@media (max-width: 640px) {
  .schedule {
    height: calc(100vh - 60px);
  }
}

.schedule__wrapper {
  min-width: 800px;
  padding-bottom: 85px;
  position: relative;
}

.schedule__lines {
  padding: 0 0 0 140px;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  min-width: 800px;
  position: absolute;
  display: flex;
  pointer-events: none;
}

.schedule__lines span {
  height: 100%;
  flex: 0 0 calc(100% / 16);
  border-left: 1px solid #1f1f1f;
}

.schedule__lines span:last-child {
  border-right: 1px solid #1f1f1f;
}

.schedule__hours {
  width: calc(100% - 140px);
  margin-left: 140px;
  top: 0;
  display: flex;
  position: sticky;
  background-color: #111;
  z-index: 3;
}

.schedule__hours > span {
  height: 40px;
  font-size: 13px;
  flex: 0 0 calc(100% / 16);
  border-left: 1px solid #1f1f1f;
  position: relative;
}

.schedule__hours > span:last-child {
  border-right: 1px solid rgba(255, 255, 255, .1);
}

.schedule__hours > span > span {
  top: 8px;
  left: -10px;
  height: 24px;
  width: 20px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  display: block;
  position: absolute;
  background-color: #111;
}

.schedule__day {
  display: block;
  position: relative;
  color: #777;

  .is-future-month &,
  .schedule__day.is-today ~ & {
    color: #fff;
  } 

  &.is-today:before {
    content: '';
    left: 0;
    top: 0;
    width: 8px;
    height: 100%;
    background-color: #d8c292;
    position: absolute;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    z-index: 10;
  }
}

.schedule__day-date {
  background-color: #111;
  width: 140px;
  font-weight: 700;
  font-size: 18px;
  padding: 0 15px;
  height: 40px;
  line-height: 40px;
  margin-top: 30px;
  top: 0;
  z-index: 2;
  position: sticky;

  .is-today & {
    color: #d8c292;
  }
}

.schedule__day-date-dayname {
  font-size: 14px;
  margin-left: .5em;
  opacity: .5;
  display: inline-block;
}

.schedule__day-room {
  width: 100%;
  display: flex;
  flex-wrap: wrap;

  .is-today & {
    color: #d8c292;
  }
}

.schedule__day-room + .schedule__item-room {
  margin-top: 20px;
}

.schedule__day-room:nth-child(2n) {
  background-color: rgba(255, 255, 255, .05);
}

.schedule__day-room:nth-child(2n+1) {
  background-color: rgba(255, 255, 255, .1);
}

.schedule__day-room-name {
  padding: 10px 15px;
  flex: 0 0 140px;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
}

.schedule__day-room-bookings {
  flex: 1 1 calc(100% - 140px);
  width: 100%;
  display: block;
}

.schedule__day-room-bookings:empty {
  display: none;
}

.schedule__day-room-triggers {
  width: 100%;
  height: 100%;
  flex: 1 1 calc(100% - 140px);
  max-width: calc(100% - 140px);
  margin-left: auto;
  display: flex;
}

.schedule__day-room-triggers div {
  height: 36px;
  flex: 0 0 calc(100% / 16);
  border-radius: 5px;
  cursor: pointer;
}

@media (min-width: 1025px) {
  .schedule__day-room-triggers div:hover {
    background-color: rgba(255, 255, 255, .5);
  }
}

/* Booking */
.schedule__day-room-booking {
  z-index: 1;
  transition: .1s;
  border-radius: 5px;
  margin-top: 10px;
  margin-bottom: 10px;
  min-width: 6.5%;
  border: 2px solid transparent;
  position: relative;
  cursor: pointer;
  display: block;
}

.schedule__day-room-booking + .schedule__day-room-booking {
  margin-top: 8px;
}

.schedule__day-room-booking-inner {
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  padding: 10px;
  border-radius: 5px;
  background-color: #fff;
  color: #111;
  overflow: hidden;
}

.schedule__day-room-booking--color-0 .schedule__day-room-booking-inner {
  background-color: #d8c292;
}

.schedule__day-room-booking--color-1 .schedule__day-room-booking-inner {
  background-color: #5eaaa8;
}

.schedule__day-room-booking--color-2 .schedule__day-room-booking-inner {
  background-color: #d4cdbe;
}

.schedule__day-room-booking--color-3 .schedule__day-room-booking-inner {
  background-color: #e1bc91;
}

.schedule__day-room-booking--color-4 .schedule__day-room-booking-inner {
  background-color: #bfc5e8;
}

.schedule__day-room-booking--color-4 .schedule__day-room-booking-inner {
  background-color: #b68973;
}

.schedule__day-room-booking strong {
  display: block;
  font-size: 14px;
}

.schedule__day-room-booking small {
  display: block;
  margin-top: 5px;
}

.schedule__day-room-booking small:empty {
  display: none;
}

.schedule__day-room-booking-tags {
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
}

.schedule__day-room-booking-tags:empty {
  display: none;
}

.schedule__day-room-booking-tag {
  padding: 2px 4px;
  border-radius: 3px;
  margin: 4px 4px 0 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .6px;
  color: #fff;
}

.schedule__day-room-booking-tag--noise {
  background-color: #ec4646;
}

.schedule__day-room-booking-tag--silence {
  background-color: #51a058;
}

.schedule__day-room-booking-remove {
  right: -8px;
  top: -8px;
  width: 30px;
  height: 30px;
  padding: 12px;
  border: 0;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 0 5px rgba(0, 0, 0, .5);
  position: absolute;
  cursor: pointer;
  display: none;
  pointer-events: none;
}

.schedule__day-room-booking-remove:before,
.schedule__day-room-booking-remove:after {
  content: '';
  left: 50%;
  top: 50%;
  width: 50%;
  height: 2px;
  background-color: #111;
  position: absolute;
  transform: translate(-50%, -50%) rotate(45deg);
}

.schedule__day-room-booking-remove:after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

@media (min-width: 1025px) {

  .schedule__day-room-booking:hover .schedule__day-room-booking-remove {
    display: block;
    pointer-events: all;
  }

  .schedule__day-room-booking-remove:hover {
    background-color: #ec4646;
  }

  .schedule__day-room-booking-remove:hover:before,
  .schedule__day-room-booking-remove:hover:after {
    background-color: #fff;
  }

}

.schedule__loading {
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  background-color: rgba(0, 0, 0, .75);
  position: absolute;
  display: none;
  align-items: center;
  justify-content: center;
}

.schedule.is-loading .schedule__loading {
  display: flex;
}

</style>
